import Vue from 'vue';
import Component from 'vue-class-component';
import {AXIOS} from "@/lib/http-common";
import ExportLayout from "@/components/properties/settings/export/model/ExportLayout";
import {DateTimeFormats} from "@/components/properties/settings/export/model/DateTimeFormats";
import JSONEditor from "jsoneditor";
import "jsoneditor/dist/jsoneditor.css";
import "./custom-jsoneditor.css";
import ExportJSONCustom from "@/components/properties/settings/export/model/ExportJSONCustom";
import UserActionResultSender from "@/components/application/UserActionResultSender";

type JsonNode = { [key: string]: any };

@Component({
  computed: {
    DateTimeFormats() {
      return DateTimeFormats;
    }
  }
})
export default class ExportStaticFieldsView extends Vue {
  public loading: boolean = false;
  public snack = false;
  public snackColor = '';
  public snackText = '';
  public exportLayoutFromDatabase: ExportLayout[] = [];
  public editExportFieldDialogOpened: boolean = false;
  public addExportFieldDialogOpened: boolean = false;
  public isEditFieldFormValid: any = null;
  public isAddFieldFormValid: any = null;
  public search = '';
  public sortBy = 'sequence';
  public disabledFields: ExportLayout[] = [];
  public jsonEditor: JSONEditor | null = null;
  public json = {};
  public dbJson = {};
  public dynamicNodeName = "";
  public updatedCustomJSON: ExportJSONCustom = this.initCustomJSON();

  public mounted() {
    this.getJSONPreview();

  }

  public unmounted() {
    if (this.jsonEditor) {
      this.jsonEditor.destroy();
    }
  }

  public initCustomJSON(): ExportJSONCustom {
    return {
      name: "Custom JSON format",
      dynamicNodeName: "items",
      dynamicNodeValue: "$items_nodes",
      dynamicNodeStaticValue: "",
      staticFields: ""
    };
  }

  public getJSONPreview() {
    this.loading = true;
    AXIOS.get('/export/json-preview')
      .then((message) => {
        if (typeof message.data === 'string') {
          // If it's a string, parse it
          this.json = JSON.parse(message.data);
        } else {
          // If it's already an object, use it as-is
          this.json = message.data;
        }
        this.dbJson = this.json;

        const container = this.$refs.jsonEditorContainer;
        if (container) {
          if (this.jsonEditor === null) {
            this.jsonEditor = new JSONEditor(container as HTMLElement, {
              mode: 'tree',
              onEditable: function(node) {
                if ('field' in node) {
                  if (node.value && node.value.toString().startsWith('$')) {
                    return {
                      field: false,
                      value: false,
                    };
                  }
                }
                return true;
              },
            });
          }
          if (this.json) {
            this.jsonEditor.set(this.json);
          }
        }

      })
      .catch(error => {
        UserActionResultSender.showErrorFeedback(error.response.data.responseCode);
      })
      .finally(() => {
        this.loading = false;
      });
  }

  public saveJson() {
    if (this.jsonEditor != null) {
      const json = this.jsonEditor.get() as JsonNode;
      this.updatedCustomJSON.dynamicNodeName = this.findDynamicNode(json);

      this.updatedCustomJSON.dynamicNodeStaticValue = JSON.stringify(this.getCustomFields());
      this.updatedCustomJSON.staticFields = JSON.stringify(this.getStaticFields());
      this.updateCustomJson(this.updatedCustomJSON);
    }
  }

  public updateCustomJson(customJson: ExportJSONCustom) {
    AXIOS.put(`/export-layout/update-custom-json`, customJson)
      .then((message) => {
        this.getJSONPreview();
        UserActionResultSender.showSuccessFeedback("export.update.success");
      })
      .catch(error => {
        UserActionResultSender.showErrorFeedback(error.response.data.responseCode);
      })
      .finally(() => {
        this.loading = false;
      });
  }

  public findDynamicNode(json: JsonNode) {
    let matchingNode: string = "";

    function traverse(node: JsonNode, path: string[] = []): void {
      if (typeof node === "object" && node !== null) {
        for (const [key, value] of Object.entries(node)) {
          const currentPath = [...path, key];
          if (typeof value === "string" && value.startsWith("$")) {
            matchingNode = currentPath[0]; // Or just save `key`
            return;
          }
          if (typeof value === "object" && value !== null) {
            traverse(value, currentPath);
          }
        }
      }
    }

    traverse(json);
    return matchingNode;
  }

  public getCustomFields(): Record<string, any> {
    if (this.jsonEditor) {
      const updatedJson = this.jsonEditor.get();
      const dynamicNodeName = this.findDynamicNode(updatedJson);

      if (dynamicNodeName && updatedJson.hasOwnProperty(dynamicNodeName)) {
        const dynamicNodeArray = updatedJson[dynamicNodeName];

        if (Array.isArray(dynamicNodeArray) && dynamicNodeArray.length > 0) {
          const firstDynamicNode = dynamicNodeArray[0];

          return Object.keys(firstDynamicNode)
            .filter((key) => !firstDynamicNode[key].toString().startsWith('$'))
            .reduce((acc, key) => {
              acc[key] = firstDynamicNode[key];
              return acc;
            }, {} as Record<string, any>);
        }
      }
    }
    return {};
  }


  public getStaticFields(): Record<string, any> {
    if (this.jsonEditor) {
      const updatedJson = this.jsonEditor.get();
      const dynamicNodeName = this.findDynamicNode(updatedJson);
      return Object.entries(updatedJson).reduce((acc, [key, value]) => {
        if (key !== dynamicNodeName) {
          acc[key] = value;
        }
        return acc;
      }, {} as Record<string, any>);
    }
    return {};
  }

  public cancel() {
    this.json = this.dbJson;
    if (this.jsonEditor) {
      this.jsonEditor.set(this.json);
    }
  }

  public reset() {
    this.updateCustomJson(this.initCustomJSON());
  }

}
